function normalizePhone(phone) {
    return String(phone || '').replace(/\s+/g, '');
}

function buildDirectionsUrl(address) {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
}

function formatInstallerAddress(installer) {
    const street = [installer.street, installer.house].filter(Boolean).join(' ').trim();
    const cityLine = [installer.post_code, installer.city].filter(Boolean).join(' ').trim();

    return [street, cityLine].filter(Boolean).join(', ');
}

function mapInstallerToCard(installer) {
    return {
        id: installer.identifier,
        name: installer.company || '',
        address: formatInstallerAddress(installer),
        phone: installer.phone || '',
        lat: installer.lat ? Number(installer.lat) : null,
        lng: installer.lng ? Number(installer.lng) : null,
        tags: Array.isArray(installer.tags) ? installer.tags : [],
    };
}

let googleMapsLoader = null;

function createMapMarkerIcon(maps, url, size = 32) {
    return {
        url,
        scaledSize: new maps.Size(size, size),
        anchor: new maps.Point(size / 2, size),
    };
}

function loadGoogleMaps(apiKey) {
    if (window.google?.maps) {
        return Promise.resolve(window.google.maps);
    }

    if (!apiKey) {
        return Promise.reject(new Error('Missing Google Maps API key'));
    }

    if (googleMapsLoader) {
        return googleMapsLoader;
    }

    googleMapsLoader = new Promise((resolve, reject) => {
        const callbackName = 'investorFindInstallerMapsInit';
        window[callbackName] = () => resolve(window.google.maps);
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=${callbackName}`;
        script.async = true;
        script.onerror = () => reject(new Error('Google Maps failed to load'));
        document.head.appendChild(script);
    });

    return googleMapsLoader;
}

function initInvestorFindInstallerFinder(section) {
    const list = section.querySelector('[data-finder-list]');
    const resultsWrap = section.querySelector('[data-finder-results]');
    const emptyWrap = section.querySelector('[data-finder-empty]');
    const summaryWrap = section.querySelector('[data-finder-summary]');
    const countEl = section.querySelector('[data-finder-count]');
    const filtersWrap = section.querySelector('[data-finder-filters]');
    const template = document.getElementById('investorFindInstallerCardTemplate');
    const mapEl = section.querySelector('[data-finder-map]');
    const heroForm = section.querySelector('[data-finder-hero-search]');
    const heroInput = section.querySelector('[data-finder-hero-input]');
    const submitButton = section.querySelector('[data-finder-submit]');
    const autocomplete = section.querySelector('[data-finder-autocomplete]');
    const radiusWrap = section.querySelector('[data-finder-radius]');
    const showAllBtn = section.querySelector('[data-finder-show-all]');

    if (!list || !resultsWrap || !emptyWrap || !template) return;

    const searchUrl = section.dataset.searchUrl || '';
    const autocompleteUrl = section.dataset.autocompleteUrl || '';
    const mapsApiKey = section.dataset.mapsApiKey || '';
    const searchRadiusKm = section.dataset.searchRadiusKm || '30';
    const maxResults = Number.parseInt(section.dataset.maxResults || '6', 10) || 6;
    const showAllLabel = section.dataset.showAll || 'Pokaż więcej';
    const mapMarkerUrl = section.dataset.mapMarkerUrl || '';
    const mapMarkerActiveUrl = section.dataset.mapMarkerActiveUrl || '';
    const msgError = section.dataset.msgError || 'Wystąpił błąd. Spróbuj ponownie później.';
    const msgAutocompleteEmpty = section.dataset.msgAutocompleteEmpty || 'Brak wyników';
    const msgAutocompleteError = section.dataset.msgAutocompleteError || 'Błąd pobierania danych';
    const radiusTitle = section.dataset.radiusTitle || '';
    const radiusTextTemplate = section.dataset.radiusText || '';
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';

    let activeFilter = 'all';
    let fetchedItems = [];
    let displayedItems = [];
    let visibleCount = maxResults;
    let mapInstance = null;
    let mapMarkers = new Map();
    let activeMarker = null;
    let autocompleteTimeout = null;
    let submitLabel = submitButton?.textContent || '';
    let hasSearched = false;
    let radiusNoticeVisible = false;

    const setPanelState = (state) => {
        resultsWrap.hidden = state !== 'results';
        emptyWrap.hidden = state !== 'empty';
        summaryWrap.hidden = state !== 'results';
    };

    const setLoading = (isLoading) => {
        if (!submitButton) return;
        submitButton.disabled = isLoading;
        submitButton.textContent = isLoading ? 'Szukam…' : submitLabel;
    };

    const hideAutocomplete = () => {
        if (!autocomplete) return;
        autocomplete.hidden = true;
        autocomplete.innerHTML = '';
    };

    const showAutocompleteMessage = (text, disabled = true) => {
        if (!autocomplete) return;
        autocomplete.innerHTML = '';
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'investor-find-installer__search-autocomplete-item';
        if (disabled) item.classList.add('is-disabled');
        item.textContent = text;
        item.disabled = disabled;
        autocomplete.appendChild(item);
        autocomplete.hidden = false;
    };

    const formatAutocompleteLabel = (item) => {
        if (item.rmName === 'post_code') {
            return `${item.name} (${item.api_voivodeship_name})`;
        }

        if (item.post_code) {
            return `${item.name} (${item.post_code}, ${item.api_voivodeship_name})`;
        }

        return `${item.name} (${item.api_voivodeship_name})`;
    };

    const getAutocompleteSearchValue = (item) => item.name;

    const filterItems = (items, filterId) => {
        if (filterId === 'all') return items;
        return items.filter((item) => Array.isArray(item.tags) && item.tags.includes(filterId));
    };

    const setActiveFilter = (filterId) => {
        activeFilter = filterId;

        filtersWrap?.querySelectorAll('[data-finder-filter]').forEach((button) => {
            const isActive = button.dataset.finderFilter === filterId;
            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
    };

    const setMarkerIcon = (marker, active = false) => {
        if (!marker || !window.google?.maps) return;

        const url = active ? (mapMarkerActiveUrl || mapMarkerUrl) : mapMarkerUrl;
        if (!url) return;

        const size = active ? 40 : 32;
        marker.setIcon(createMapMarkerIcon(window.google.maps, url, size));
    };

    const setActiveCard = (card) => {
        list.querySelectorAll('[data-finder-card]').forEach((item) => {
            item.classList.toggle('is-active', item === card);
        });

        if (!card || !mapEl) return;

        const installerId = card.dataset.installerId || '';
        mapEl.dataset.activeId = installerId;

        if (activeMarker) {
            setMarkerIcon(activeMarker, false);
            activeMarker = null;
        }

        const marker = mapMarkers.get(installerId);
        if (marker && mapInstance) {
            activeMarker = marker;
            setMarkerIcon(marker, true);
            mapInstance.panTo(marker.getPosition());
        }
    };

    const updateRadiusNotice = (nothingFound) => {
        if (!radiusWrap) return;

        if (!nothingFound) {
            radiusWrap.hidden = true;
            return;
        }

        const titleEl = radiusWrap.querySelector('.investor-find-installer__finder-radius-title');
        const textEl = radiusWrap.querySelector('.investor-find-installer__finder-radius-text');

        if (titleEl && radiusTitle) {
            titleEl.textContent = radiusTitle;
        }

        if (textEl && radiusTextTemplate) {
            textEl.textContent = radiusTextTemplate.replace(':radius', searchRadiusKm);
        }

        radiusWrap.hidden = false;
    };

    const getVisibleListItems = (items) => {
        if (!maxResults) {
            return items;
        }

        return items.slice(0, visibleCount);
    };

    const updateShowAllButton = (totalCount) => {
        if (!showAllBtn) return;

        showAllBtn.hidden = totalCount <= visibleCount;
        showAllBtn.textContent = showAllLabel;
    };

    const focusItemOnMap = (item) => {
        let card = list.querySelector(`[data-installer-id="${CSS.escape(String(item.id))}"]`);

        if (!card && visibleCount < displayedItems.length) {
            visibleCount = displayedItems.length;
            renderListCards(displayedItems);
            card = list.querySelector(`[data-installer-id="${CSS.escape(String(item.id))}"]`);
        }

        if (card) setActiveCard(card);
    };

    const renderListCards = (items) => {
        const visibleItems = getVisibleListItems(items);

        list.innerHTML = '';

        visibleItems.forEach((item, index) => {
            const node = template.content.firstElementChild.cloneNode(true);

            node.dataset.installerId = item.id || `installer-${index}`;
            node.querySelector('[data-finder-card-name]').textContent = item.name || '';
            node.querySelector('[data-finder-card-address]').textContent = item.address || '';
            node.querySelector('[data-finder-card-phone]').textContent = item.phone || '';

            const phone = normalizePhone(item.phone);
            const callLink = node.querySelector('[data-finder-card-call]');
            const directionsLink = node.querySelector('[data-finder-card-directions]');
            const focusButton = node.querySelector('[data-finder-card-focus]');
            const phoneRow = node.querySelector('[data-finder-card-phone]')?.closest('.investor-find-installer__finder-card-row');

            if (callLink) {
                if (phone) {
                    callLink.href = `tel:${phone}`;
                } else {
                    callLink.hidden = true;
                }
            }

            if (!item.phone && phoneRow) {
                phoneRow.hidden = true;
            }

            if (directionsLink && item.address) {
                directionsLink.href = buildDirectionsUrl(item.address);
            }

            node.addEventListener('click', (event) => {
                if (event.target.closest('a, button')) return;
                setActiveCard(node);
            });

            focusButton?.addEventListener('click', () => {
                setActiveCard(node);
                mapEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            });

            list.appendChild(node);

            if (index === 0) {
                setActiveCard(node);
            }
        });

        updateShowAllButton(items.length);
    };

    const renderResults = (items) => {
        displayedItems = items;

        if (!items.length) {
            updateRadiusNotice(false);
            setPanelState(hasSearched ? 'empty' : 'idle');
            updateMap([]);
            updateShowAllButton(0);
            if (countEl) countEl.textContent = '0';
            return;
        }

        setPanelState('results');
        updateRadiusNotice(radiusNoticeVisible);
        if (countEl) countEl.textContent = String(items.length);
        renderListCards(items);
        updateMap(items);
    };

    const updateMap = (items) => {
        if (!mapEl || !mapsApiKey) return;

        const mappable = items.filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng));

        loadGoogleMaps(mapsApiKey)
            .then((maps) => {
                if (!mapInstance) {
                    mapInstance = new maps.Map(mapEl, {
                        center: { lat: 52.069, lng: 19.48 },
                        zoom: 6,
                        mapTypeControl: false,
                        streetViewControl: false,
                        fullscreenControl: false,
                    });
                    mapEl.classList.add('is-ready');
                }

                mapMarkers.forEach((marker) => marker.setMap(null));
                mapMarkers.clear();
                activeMarker = null;

                if (!mappable.length) {
                    mapInstance.setCenter({ lat: 52.069, lng: 19.48 });
                    mapInstance.setZoom(6);
                    return;
                }

                const bounds = new maps.LatLngBounds();

                mappable.forEach((item) => {
                    const position = { lat: item.lat, lng: item.lng };
                    const marker = new maps.Marker({
                        map: mapInstance,
                        position,
                        title: item.name,
                        ...(mapMarkerUrl && {
                            icon: createMapMarkerIcon(maps, mapMarkerUrl, 32),
                        }),
                    });

                    marker.addListener('click', () => {
                        focusItemOnMap(item);
                    });

                    mapMarkers.set(String(item.id), marker);
                    bounds.extend(position);
                });

                if (mappable.length === 1) {
                    mapInstance.setCenter(bounds.getCenter());
                    mapInstance.setZoom(14);
                } else {
                    mapInstance.fitBounds(bounds, 48);
                }

                const firstCard = list.querySelector('[data-finder-card]');
                if (firstCard) setActiveCard(firstCard);
            })
            .catch((error) => {
                console.error('Mapa Google:', error);
            });
    };

    const applyFilter = (filterId) => {
        setActiveFilter(filterId);
        if (!hasSearched) return;
        visibleCount = maxResults;
        renderResults(filterItems(fetchedItems, filterId));
    };

    const runSearch = (localizationId = '', localizationName = heroInput?.value?.trim() || '') => {
        hideAutocomplete();

        if (!localizationName) {
            hasSearched = false;
            radiusNoticeVisible = false;
            fetchedItems = [];
            displayedItems = [];
            visibleCount = maxResults;
            updateRadiusNotice(false);
            setPanelState('idle');
            updateMap([]);
            if (countEl) countEl.textContent = '0';
            return;
        }

        if (!searchUrl) return;

        hasSearched = true;
        visibleCount = maxResults;
        setLoading(true);

        fetch(searchUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                'X-Requested-With': 'XMLHttpRequest',
                ...(csrfToken && { 'X-CSRF-Token': csrfToken }),
            },
            body: new URLSearchParams({
                localizationName,
                localizationId: localizationId || '',
            }),
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.code !== 200) {
                    throw new Error(data.message || msgError);
                }

                const installers = Array.isArray(data.installersForMap)
                    ? data.installersForMap.filter(Boolean)
                    : [];

                fetchedItems = installers.map(mapInstallerToCard);
                radiusNoticeVisible = Boolean(data.nothingFound) && fetchedItems.length > 0;

                if (data.localizationName && heroInput) {
                    heroInput.value = data.localizationName;
                }

                applyFilter(activeFilter);
            })
            .catch((error) => {
                console.error('Finder:', error);
                alert(msgError);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    showAllBtn?.addEventListener('click', () => {
        visibleCount += maxResults;
        renderListCards(displayedItems);
    });

    filtersWrap?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-finder-filter]');
        if (!button) return;

        applyFilter(button.dataset.finderFilter || 'all');
    });

    heroForm?.addEventListener('submit', (event) => {
        event.preventDefault();
        runSearch();
    });

    heroInput?.addEventListener('input', () => {
        const query = heroInput.value.trim();
        clearTimeout(autocompleteTimeout);

        if (!autocomplete || !autocompleteUrl || query.length < 2) {
            hideAutocomplete();
            return;
        }

        autocompleteTimeout = setTimeout(() => {
            fetch(`${autocompleteUrl}?query=${encodeURIComponent(query)}`)
                .then((response) => response.json())
                .then((data) => {
                    const items = Array.isArray(data) ? data : Object.values(data || {});

                    if (!items.length) {
                        showAutocompleteMessage(msgAutocompleteEmpty);
                        return;
                    }

                    autocomplete.innerHTML = '';

                    items.forEach((item) => {
                        const button = document.createElement('button');
                        button.type = 'button';
                        button.className = 'investor-find-installer__search-autocomplete-item';
                        button.textContent = formatAutocompleteLabel(item);
                        button.dataset.localizationId = item.id || '';
                        button.dataset.localizationName = getAutocompleteSearchValue(item);
                        autocomplete.appendChild(button);
                    });

                    autocomplete.hidden = false;
                })
                .catch(() => {
                    showAutocompleteMessage(msgAutocompleteError);
                });
        }, 200);
    });

    autocomplete?.addEventListener('click', (event) => {
        const item = event.target.closest('.investor-find-installer__search-autocomplete-item');
        if (!item || item.classList.contains('is-disabled')) return;

        if (heroInput) {
            heroInput.value = item.dataset.localizationName || item.textContent;
        }

        hideAutocomplete();
        runSearch(item.dataset.localizationId || '', item.dataset.localizationName || heroInput?.value?.trim() || '');
    });

    document.addEventListener('click', (event) => {
        if (event.target.closest('[data-finder-hero-input], [data-finder-autocomplete]')) return;
        hideAutocomplete();
    });

    setPanelState('idle');
    updateMap([]);
}

document.addEventListener('DOMContentLoaded', () => {
    const finder = document.querySelector('[data-finder]');
    if (finder) initInvestorFindInstallerFinder(finder);
});
